/**
 * Step Data Extractors
 * Standardized functions to extract and normalize data from agent results
 */

/**
 * Extract data from nested agent response structures
 * Handles various response formats from the backend
 */
const extractFromAgentResponse = (response) => {
  if (!response) return null;

  // Handle different response structures:
  // 1. response.result.result (nested from backend)
  // 2. response.result (direct result)
  // 3. response (raw data)

  if (response.result && response.result.result) {
    return response.result.result;
  }
  if (response.result && typeof response.result === 'object') {
    return response.result;
  }
  return response;
};

/**
 * Step 1: Item Definition Data Extractor
 */
export const extractStep1Data = (agentResponse) => {
  const rawData = extractFromAgentResponse(agentResponse);
  if (!rawData) return null;

  // Handle both old and new formats
  if (rawData.item_definition) {
    // New structured format
    const identity = rawData.item_definition.system_level_information?.item_identity;
    const scope = rawData.item_definition.scope_definition;

    return {
      itemName: identity?.name || '',
      itemDescription: identity?.description || '',
      itemPurpose: identity?.purpose || scope?.purpose || '',
      itemScope: scope?.scope || '',
      components: rawData.item_definition.components || [],
      interfaces: rawData.item_definition.interfaces || [],
      dataFlows: rawData.item_definition.data_flows || [],
      confidence: rawData.overall_confidence || 0,
      timestamp: agentResponse.completed_at || new Date().toISOString()
    };
  }

  // Old format (direct fields)
  return {
    itemName: rawData.item_name || rawData.itemName || '',
    itemDescription: rawData.item_description || rawData.itemDescription || '',
    itemPurpose: rawData.item_purpose || rawData.itemPurpose || '',
    itemScope: rawData.item_scope || rawData.itemScope || '',
    components: rawData.components || [],
    interfaces: rawData.interfaces || [],
    dataFlows: rawData.data_flows || rawData.dataFlows || [],
    confidence: rawData.overall_confidence || rawData.confidence || 0,
    timestamp: agentResponse.completed_at || new Date().toISOString()
  };
};

/**
 * Step 2: Asset Identification Data Extractor
 */
export const extractStep2Data = (agentResponse) => {
  const rawData = extractFromAgentResponse(agentResponse);
  if (!rawData) return [];

  // Handle array or object with assets property
  const assetsArray = Array.isArray(rawData) ? rawData : (rawData.assets || rawData.cybersecurity_assets || []);

  // Preserve ALL backend fields (including hierarchical: source, is_relevant, etc.)
  return assetsArray.map((asset, index) => ({
    ...asset,  // ← Preserve all original fields first (including hierarchical metadata)
    // Then add normalized/fallback fields for compatibility
    asset_id: asset.asset_id || asset.id || `ASSET_${index + 1}`,
    asset_name: asset.asset_name || asset.name || '',
    asset_type: asset.asset_type || asset.type || '',
    asset_description: asset.asset_description || asset.description || '',
    // Security properties per ISO 21434 (Confidentiality, Integrity, Availability, etc.)
    security_properties: asset.security_properties || []
  }));
};

/**
 * Step 3: Damage Scenarios Data Extractor
 */
export const extractStep3Data = (agentResponse) => {
  const rawData = extractFromAgentResponse(agentResponse);
  if (!rawData) return [];

  const scenariosArray = Array.isArray(rawData) ? rawData : (rawData.damage_scenarios || rawData.scenarios || []);

  // Preserve ALL backend fields (including hierarchical metadata)
  return scenariosArray.map((scenario, index) => ({
    ...scenario,  // ← Preserve all original fields
    damage_id: scenario.damage_id || `DAMAGE_${index + 1}`,
    asset_ids: scenario.asset_ids || [],
    compromised_properties: scenario.compromised_properties || [],
    damage_scenario: scenario.damage_scenario || '',
    safety: scenario.safety || 'Negligible',
    financial: scenario.financial || 'Negligible',
    operational: scenario.operational || 'Negligible',
    privacy: scenario.privacy || 'Negligible',
    final_impact_rating: scenario.final_impact_rating || 'Moderate',
  }));
};

/**
 * Step 4: Threat Identification Data Extractor
 */
export const extractStep4Data = (agentResponse) => {
  const rawData = extractFromAgentResponse(agentResponse);
  if (!rawData) return [];

  const threatsArray = Array.isArray(rawData) ? rawData : (rawData.threats || rawData.threats_identified || []);

  // Preserve ALL backend fields (including hierarchical metadata)
  return threatsArray.map((threat, index) => ({
    ...threat,  // ← Preserve all original fields
    threat_id: threat.threat_id || `THREAT_${index + 1}`,
    asset_id: threat.asset_id || '',
    damage_ids: threat.damage_ids || [],
    stride_category: threat.stride_category || '',
    compromised_property: threat.compromised_property || '',
    threat_scenario: threat.threat_scenario || '',
    threat_source: threat.threat_source || threat.threatSource || 'Custom'
  }));
};

/**
 * Step 5: Attack Paths Data Extractor
 */
export const extractStep5Data = (agentResponse) => {
  const rawData = extractFromAgentResponse(agentResponse);
  if (!rawData) return [];

  // Handle new API structure with attack_analysis array
  const pathsArray = Array.isArray(rawData) ? rawData :
    (rawData.attack_analysis || rawData.attack_paths || rawData.paths || []);

  // Preserve ALL backend fields (including hierarchical metadata)
  return pathsArray.map((path, index) => ({
    ...path,  // ← Preserve all original fields
    // Use backend field names directly
    attack_path_id: path.attack_path_id || path.id || `ATTACK_${index + 1}`,
    threat_id: path.threat_id || path.threatId || '',
    asset_id: path.asset_id || path.targetAsset || '',
    attack_path: path.attack_path || path.description || '',
    attack_vector: path.attack_vector || path.attackVector || '',
    threat_agent: path.threat_agent || path.threatAgent || '',
    
    // Attack potential factors
    elapsed_time: path.elapsed_time || path.time_required || '',
    specialist_expertise: path.specialist_expertise || path.expertise || path.skill_level || '',
    knowledge_of_item: path.knowledge_of_item || path.knowledge || path.knowledgeRequired || '',
    window_of_opportunity: path.window_of_opportunity || path.window || '',
    equipment: path.equipment || '',
    
    // Scoring fields
    total_attack_potential: path.total_attack_potential || path.attack_potential || 0,
    attack_feasibility_rating: path.attack_feasibility_rating || path.feasibility_rating || path.feasibility || 'Medium',
    
    // Additional fields
    preconditions: path.preconditions || path.prerequisites || [],
    justification: path.justification || '',
    
    // Numeric scoring values (calculated by backend)
    elapsed_time_value: path.elapsed_time_value || 0,
    expertise_value: path.expertise_value || 0,
    knowledge_value: path.knowledge_value || 0,
    window_value: path.window_value || 0,
    equipment_value: path.equipment_value || 0
  }));
};

/**
 * Step 6: Risk Assessment Data Extractor
 */
export const extractStep6Data = (agentResponse) => {
  const rawData = extractFromAgentResponse(agentResponse);
  if (!rawData) return [];

  const risksArray = Array.isArray(rawData) ? rawData : (rawData.risks || rawData.risk_assessments || []);

  // Preserve ALL backend fields (including hierarchical metadata)
  return risksArray.map((risk, index) => ({
    ...risk,  // ← Preserve all original fields
    // Map to exact field names expected by the table columns (matching API response)
    risk_id: risk.risk_id || risk.id || `risk_${index}`,
    asset_id: risk.asset_id || risk.assetId || '',
    threat_id: risk.threat_id || risk.threatId || '',
    damage_id: risk.damage_id || risk.damage_scenario_id || risk.scenarioId || '',
    risk_value: risk.risk_value || risk.risk_score || risk.riskScore || 0,
    treatment_decision: risk.treatment_decision || risk.treatment_option || 'Reduce',
    treatment_priority: risk.treatment_priority || risk.priority || 'Medium',
    treatment_justification: risk.treatment_justification || risk.justification || '',

    // Additional fields for backward compatibility if needed
    id: risk.risk_id || risk.id || `risk_${index}`
  }));
};

/**
 * Step 7: Cybersecurity Goals & Claims Data Extractor (ISO 21434 Clause 9.4)
 * - Goals (RQ-09-05): For "Reduce" treatment decisions
 * - Claims (RQ-09-06): For "Share" or "Retain" treatment decisions
 */
export const extractStep7Data = (agentResponse) => {
  const rawData = extractFromAgentResponse(agentResponse);
  if (!rawData) return { goals: [], claims: [] };

  return {
    goals: extractGoals(rawData),
    claims: extractClaims(rawData)
  };
};

/**
 * Extract Cybersecurity Goals (RQ-09-05)
 * Goals are generated for "Reduce" treatment decisions
 */
const extractGoals = (rawData) => {
  const goalsArray = rawData.cybersecurity_goals || rawData.goals || [];

  return goalsArray.map((goal, index) => ({
    ...goal,  // ← Preserve ALL backend fields including hierarchical metadata
    goal_id: goal.goal_id || `CSG-${String(index + 1).padStart(2, '0')}`,
    goal_statement: goal.goal_statement || '',
    risk_ids: goal.risk_ids || [],
    security_property: goal.security_property || '',
    cal_level: goal.cal_level || ''
    // Hierarchical fields (if present): source, source_component, source_session_id, 
    // is_relevant, relevance_justification, affected_components
  }));
};

/**
 * Extract Cybersecurity Claims (RQ-09-06)
 * Claims are generated for "Share" or "Retain" treatment decisions
 */
const extractClaims = (rawData) => {
  const claimsArray = rawData.cybersecurity_claims || rawData.claims || [];

  return claimsArray.map((claim, index) => ({
    ...claim,  // ← Preserve ALL backend fields including hierarchical metadata
    claim_id: claim.claim_id || `CLAIM-${String(index + 1).padStart(2, '0')}`,
    claim_statement: claim.claim_statement || '',
    justification: claim.justification || '',
    risk_ids: claim.risk_ids || []
    // Hierarchical fields (if present): source, source_component, source_session_id,
    // is_relevant, relevance_justification
  }));
};

/**
 * Step 8: Cybersecurity Concept Data Extractor (ISO 21434 Clause 9.5)
 * - Controls (RQ-09-08): Mechanisms to achieve goals
 * - Requirements (RQ-09-09): Specifications implementing controls
 */
export const extractStep8Data = (agentResponse) => {
  const rawData = extractFromAgentResponse(agentResponse);
  if (!rawData) return { controls: [], requirements: [], skipped: false };

  // Check if step was legitimately skipped (no Reduce risks)
  const isSkipped = rawData.skipped === true;
  const skipReason = rawData.reason || null;

  // Extract controls and requirements
  const controls = extractControls(rawData);
  const requirements = extractRequirements(rawData);

  // If there's actual data, it's not skipped regardless of the flag
  const hasActualData = controls.length > 0 || requirements.length > 0;

  return {
    controls,
    requirements,
    skipped: isSkipped && !hasActualData,
    skipReason: (isSkipped && !hasActualData) ? skipReason : null
  };
};

/**
 * Extract Cybersecurity Controls (RQ-09-08)
 * Controls describe mechanisms to achieve cybersecurity goals
 */
const extractControls = (rawData) => {
  const controlsArray = rawData.cybersecurity_controls || rawData.controls || [];

  return controlsArray.map((control, index) => ({
    ...control,  // ← Preserve ALL backend fields including hierarchical metadata
    control_id: control.control_id || `CTRL-${String(index + 1).padStart(2, '0')}`,
    control_name: control.control_name || '',
    control_description: control.control_description || '',
    control_type: control.control_type || '',
    goal_id: control.goal_id || ''
    // Hierarchical fields (if present): source, source_component, source_session_id,
    // is_relevant, relevance_justification, affected_components
  }));
};

/**
 * Extract Cybersecurity Requirements (RQ-09-09)
 * Requirements implement controls
 */
const extractRequirements = (rawData) => {
  const requirementsArray = rawData.cybersecurity_requirements || rawData.requirements || [];

  return requirementsArray.map((req, index) => ({
    ...req,  // ← Preserve ALL backend fields including hierarchical metadata
    req_id: req.req_id || `REQ-${String(index + 1).padStart(2, '0')}`,
    req_title: req.req_title || '',
    req_statement: req.req_statement || '',
    req_rationale: req.req_rationale || '',
    requirement_type: req.requirement_type || 'item',
    control_id: req.control_id || '',
    cal_level: req.cal_level || ''
    // Hierarchical fields (if present): source, source_component, source_session_id,
    // is_relevant, relevance_justification, affected_components
  }));
};

/**
 * Step 9: Final Report Data Extractor
 */
export const extractStep9Data = (agentResponse) => {
  const rawData = extractFromAgentResponse(agentResponse);
  if (!rawData) return null;

  return {
    executive_summary: rawData.executive_summary || rawData.executiveSummary || '',
    scope: rawData.scope || '',
    methodology: rawData.methodology || '',
    key_findings: rawData.key_findings || rawData.keyFindings || [],
    risk_summary: rawData.risk_summary || rawData.riskSummary || {},
    recommendations: rawData.recommendations || [],
    compliance_status: rawData.compliance_status || rawData.complianceStatus || {},
    next_steps: rawData.next_steps || rawData.nextSteps || [],
    appendices: rawData.appendices || [],
    generated_at: rawData.generated_at || new Date().toISOString(),
    version: rawData.version || '1.0.0',
    classification: rawData.classification || 'Confidential',
    distribution: rawData.distribution || []
  };
};

/**
 * Generic fallback extractor
 */
export const extractGenericData = (agentResponse) => {
  const rawData = extractFromAgentResponse(agentResponse);
  if (!rawData) return null;

  // Return raw data as-is for components to handle
  return rawData;
};

/**
 * Helper to check if data extraction was successful
 */
export const isValidExtraction = (data) => {
  if (!data) return false;
  if (Array.isArray(data)) return data.length > 0;
  if (typeof data === 'object') return Object.keys(data).length > 0;
  return true;
};